import { useState, useEffect } from "react";
import { Bell, X } from "lucide-react";
import { usePushSubscription } from "../hooks/usePushSubscription";
import { useAppContext } from "../context";

const DISMISSED_KEY = "push_prompt_dismissed";

export default function PushSubscribePrompt() {
  const { customerUser } = useAppContext();
  const { supported, permission, subscribed, loading, subscribe } =
    usePushSubscription(customerUser?.id || null);

  const [visible, setVisible] = useState(false);

  // Show once ever — use localStorage so it survives across sessions
  useEffect(() => {
    if (!supported) return;
    if (permission === "denied") return;
    if (subscribed) return;
    if (localStorage.getItem(DISMISSED_KEY)) return;

    const t = setTimeout(() => setVisible(true), 2000);
    return () => clearTimeout(t);
  }, [supported, permission, subscribed]);

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem(DISMISSED_KEY, "1");
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-50 w-full max-w-sm mx-4 animate-fade-in">
      <div className="bg-white border border-gray-100 rounded-2xl shadow-2xl p-4 flex items-start gap-3">
        {/* Icon */}
        <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center flex-shrink-0">
          <Bell size={20} className="text-orange-500" />
        </div>

        {/* Text */}
        <div className="flex-1 min-w-0">
          <p className="font-bold text-gray-900 text-sm">Stay in the loop!</p>
          <p className="text-xs text-gray-500 mt-0.5">
            Get notified about flash sales, order updates, and exclusive deals.
          </p>
          <div className="flex gap-2 mt-3">
            <button
              onClick={async () => { await subscribe(); dismiss(); }}
              disabled={loading}
              className="flex-1 bg-orange-500 hover:bg-orange-600 text-white text-xs font-bold py-2 rounded-xl transition disabled:opacity-50"
            >
              {loading ? "Setting up…" : "Allow notifications"}
            </button>
            <button
              onClick={dismiss}
              className="px-3 py-2 text-xs font-semibold text-gray-500 hover:text-gray-700 rounded-xl hover:bg-gray-100 transition"
            >
              Not now
            </button>
          </div>
        </div>

        {/* Close */}
        <button
          onClick={dismiss}
          className="p-1 rounded-full hover:bg-gray-100 text-gray-400 transition flex-shrink-0"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
